import { real_id } from '../interface_helpers';
import { getTemplateComponents, getTemplateService, isXT1Module } from './template_helpers';
//Types
import type { Device } from 'src/types/Device';
import type { TemplateGroupType, VirtualComponentTypes } from 'src/types/VirtualComponents';

export type GroupType = 'relay' | 'light' | 'roller' | 'thermostat';
export type JRPCCommand = {
	deviceId: string;
	method: string;
	params: {
		id: number;
		on?: boolean;
		value?: any;
	};
};
type GroupRequestType = 'switch' | 'cover' | 'light';
type DevicesList = Record<string, { ids: string[] }>;
type XT1GroupCommandValue = string | number | boolean;

export type XT1LocalGroupRequest = {
	method: string;
	params: {
		id: number;
		value: XT1GroupCommandValue;
	};
};

const mapXT1TemplateGroupTypeToAppType = (groupType: TemplateGroupType): GroupType => {
	switch (groupType) {
		case 'switch':
			return 'relay';
		case 'cover':
			return 'roller';
		default:
			return groupType;
	}
};

export const getXT1AppGroupTypes = (device: Device): string[] => {
	const templateService = getTemplateService(device);
	const templateGroups = templateService?.groups || [];
	return templateGroups.map((group) => mapXT1TemplateGroupTypeToAppType(group.type));
};

export const isXT1GroupSupported = (device: Device, groupType: GroupType): boolean => {
	return groupType === device?.category || getXT1AppGroupTypes(device).includes(groupType);
};

export const extractJRPCGroupCommands = (
	element: VirtualComponentTypes,
	data,
	commands,
): JRPCCommand[] => {
	const ids: string[] = data?.ids || [];
	const value = commands?.on ?? commands?.brightness ?? commands?.position;
	const method = element.replace(/^jrpc_/, '');
	return ids
		.map((deviceID) => {
			const match = deviceID?.match?.(/_(\d+)$/);
			const id = Number(match?.[1]);
			if (!Number.isFinite(id)) {
				return null;
			}

			return {
				deviceId: real_id(deviceID),
				method: [method, `set`].join(`.`),
				params: {
					id,
					...(method === 'switch' ? { on: commands?.on } : { value }),
				},
			};
		})
		.filter((command) => Boolean(command));
};

export const getXT1GroupCommandComponent = (device: Device, command: string) => {
	return getTemplateComponents(
		device,
		null,
		new RegExp(`^${command === 'on' ? 'state' : command}$`),
	)?.[0];
};

export const addXT1DeviceToGroupRequest = (
	device: Device,
	devicesList: DevicesList,
	data: any,
): DevicesList => {
	if (!isXT1Module(device?.type) || !device?.component) {
		return devicesList;
	}

	const add = (
		type: GroupRequestType | VirtualComponentTypes | 'jrpc_switch',
		index: number,
		keepZero = false,
	) => {
		if (!Number.isFinite(index)) return;
		const group = (devicesList[type] ??= { ids: [] });
		const realID = real_id(device.id);
		const id = index > 0 || keepZero ? `${realID}_${index}` : realID;
		if (!group.ids.includes(id)) {
			group.ids.push(id);
		}
	};

	const [componentType, componentID = '0'] = device.component.split(':');
	if (['cct', 'light', 'rgb', 'rgbw', 'rgbcct'].includes(componentType)) {
		add('light', device.channel);
	} else if (componentType === 'switch') {
		add('jrpc_switch', Number(componentID), true);
	} else if (componentType === 'cover') {
		add('cover', device.channel);
	} else if (componentType === 'service') {
		const commands = Object.keys(data || {});
		commands.forEach((command) => {
			const component = getXT1GroupCommandComponent(device, command);
			if (component) {
				add(component.type, Number(component.config?.id ?? componentID));
			}
		});
	}
	return devicesList;
};

export const getXT1LocalGroupRequests = (
	device: Device,
	data: Record<string, XT1GroupCommandValue>,
): XT1LocalGroupRequest[] => {
	const requests: XT1LocalGroupRequest[] = [];
	for (const [command, value] of Object.entries(data)) {
		const component = getXT1GroupCommandComponent(device, command);
		const id = Number(component?.config?.id);
		if (!component || !Number.isFinite(id)) continue;
		requests.push({
			method: `${component.type}.set`,
			params: { id, value },
		});
	}
	return requests;
};
